import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import GradeCardService from "../../../_services/GradeCardService";
import ItemInputFactory from "./ItemInputFactory";

interface StudentRow {
  EnquiryId: number;
  EnrollmentId: number;
  StudentName: string;
}

interface Props {
  students: StudentRow[];
  structure: any;
  structureId: number;
  termIndex: number;
  yearId?: number;
  onSaved: () => void;
}

/**
 * Bulk entry grid: select an item from the sidebar, then fill values for all students.
 */
const BulkEntryGrid: React.FC<Props> = ({
  students,
  structure,
  structureId,
  termIndex,
  yearId,
  onSaved,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [values, setValues] = useState<Record<number, string | null>>({}); // enquiryId -> value
  const [saving, setSaving] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);

  // Flatten all items for sidebar
  const allSections: Array<{ section: any; items: any[] }> = (
    structure.sections || []
  ).map((sec: any) => ({
    section: sec,
    items: sec.items || [],
  }));

  const selectedItem = allSections
    .flatMap((s) => s.items)
    .find((i: any) => i.Id === selectedItemId);

  const selectedSection = allSections.find((s) =>
    s.items.some((i: any) => i.Id === selectedItemId),
  )?.section;

  // Load existing entries for selected item
  useEffect(() => {
    if (!selectedItemId) return;
    loadEntriesForItem();
  }, [selectedItemId]);

  const loadEntriesForItem = async () => {
    setLoadingEntries(true);
    const newValues: Record<number, string | null> = {};

    try {
      // Load entries for all students (we can batch this, but for simplicity load per-student)
      const promises = students.map(async (student) => {
        try {
          const res = await GradeCardService.getStudentEntries(
            student.EnquiryId,
            { structureId, yearId },
          );
          const entries = res.data?.data?.entries || [];
          const match = entries.find(
            (e: any) =>
              e.ItemId === selectedItemId && e.TermIndex === termIndex,
          );
          if (match) {
            newValues[student.EnquiryId] = match.Value;
          }
        } catch {
          // Ignore per-student errors
        }
      });
      await Promise.all(promises);
    } catch (e) {
      console.error("Failed to load bulk entries", e);
    } finally {
      setValues(newValues);
      setLoadingEntries(false);
    }
  };

  const resolveRatingScale = () => {
    if (selectedItem?.RatingScaleOptions) {
      return typeof selectedItem.RatingScaleOptions === "string"
        ? JSON.parse(selectedItem.RatingScaleOptions)
        : selectedItem.RatingScaleOptions;
    }
    if (selectedSection?.RatingScaleOptions) {
      return typeof selectedSection.RatingScaleOptions === "string"
        ? JSON.parse(selectedSection.RatingScaleOptions)
        : selectedSection.RatingScaleOptions;
    }
    if (structure.DefaultRatingScaleOptions) {
      return typeof structure.DefaultRatingScaleOptions === "string"
        ? JSON.parse(structure.DefaultRatingScaleOptions)
        : structure.DefaultRatingScaleOptions;
    }
    return [];
  };

  const handleSaveAll = async () => {
    if (!selectedItemId) return;
    setSaving(true);
    try {
      const promises = students.map((student) =>
        GradeCardService.saveEntries({
          enquiryId: student.EnquiryId,
          enrollmentId: student.EnrollmentId,
          structureId,
          termIndex,
          yearId,
          entries: [
            { itemId: selectedItemId, value: values[student.EnquiryId] ?? null },
          ],
        }),
      );
      await Promise.all(promises);
      onSaved();
    } catch (e: any) {
      alert(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 16, minHeight: 400 }}>
      {/* Sidebar: sections > items */}
      <div
        style={{
          width: 260,
          borderRight: "1px solid var(--border-default)",
          paddingRight: 12,
          overflowY: "auto",
          maxHeight: "calc(100vh - 300px)",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>
          Select Item
        </div>
        {allSections.map(({ section, items }) => (
          <div key={section.Id} style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--text-body)",
                marginBottom: 4,
              }}
            >
              {section.Name}
            </div>
            {items.map((item: any) => (
              <div
                key={item.Id}
                onClick={() => setSelectedItemId(item.Id)}
                style={{
                  padding: "4px 8px",
                  fontSize: 12,
                  cursor: "pointer",
                  borderRadius: 4,
                  background:
                    selectedItemId === item.Id ? "#0d6efd" : "transparent",
                  color: selectedItemId === item.Id ? "#fff" : "#333",
                  marginBottom: 2,
                }}
              >
                {item.Name}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Main: student grid */}
      <div style={{ flex: 1 }}>
        {!selectedItemId ? (
          <div className="text-center text-muted" style={{ paddingTop: 60 }}>
            Select an item from the sidebar to enter values for all students.
          </div>
        ) : loadingEntries ? (
          <div className="text-center py-4">Loading entries...</div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <div>
                <strong>{selectedItem?.Name}</strong>
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 11,
                    color: "var(--text-faint)",
                  }}
                >
                  ({selectedItem?.ItemType})
                </span>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveAll}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save All"}
              </Button>
            </div>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th style={{ width: 200 }}>Student</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student.EnquiryId}>
                    <td>{student.StudentName}</td>
                    <td>
                      <ItemInputFactory
                        itemType={selectedItem?.ItemType || "text"}
                        options={
                          typeof selectedItem?.Options === "string"
                            ? JSON.parse(selectedItem.Options)
                            : selectedItem?.Options
                        }
                        ratingScaleOptions={resolveRatingScale()}
                        value={values[student.EnquiryId] ?? null}
                        onChange={(v) =>
                          setValues((prev) => ({
                            ...prev,
                            [student.EnquiryId]: v,
                          }))
                        }
                        compact
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default BulkEntryGrid;
